import { useEffect, useRef } from 'react';
import { getServiceWorkerContainer } from '@/lib/serviceWorkerSupport';

/**
 * Hook to register and manage service worker for offline asset caching
 */
export function useServiceWorker() {
  const updateIntervalRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    const serviceWorker = getServiceWorkerContainer();

    if (serviceWorker) {
      const registerSW = async () => {
        try {
          // Avoid noisy rejections when the script is temporarily unavailable during deploys.
          const scriptProbe = await fetch('/service-worker.js', { cache: 'no-store' });
          if (!scriptProbe.ok) {
            return;
          }

          const registration = await serviceWorker.register('/service-worker.js');
          if (!mounted) return;

          // Check for updates periodically
          updateIntervalRef.current = window.setInterval(() => {
            if (!navigator.onLine) return;
            registration.update().catch(() => {
              // Transient network issues or deploy races can reject update checks.
            });
          }, 60000); // Check every minute
        } catch {
          // Service worker registration failed silently.
        }
      };

      // If page already loaded, register immediately; otherwise wait for load
      if (document.readyState === 'complete') {
        void registerSW();
      } else {
        const onLoad = () => {
          void registerSW();
        };
        window.addEventListener('load', onLoad);
        return () => {
          mounted = false;
          window.removeEventListener('load', onLoad);
          if (updateIntervalRef.current) {
            clearInterval(updateIntervalRef.current);
          }
        };
      }
    }

    return () => {
      mounted = false;
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);
}
